import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as ROUTES from '@/shared/constants/routes';
import { DataTable, StatusField, Icon, type Column, type MobileConfig, EmptyState } from '@chikara/ui';
import { formatCurrency, formatDateExact } from '@chikara/utils';
import dataNotFound from '@/features/card/assets/empty-list-icon.svg';
import { Card } from '@/features/card/types/card.types';
import styles from './CardAssignedTable.module.css';
import { getStatusType } from '@/features/card/utils/tableStatus';
import { ApproveCardModal } from '@/features/card/components/ApproveCardModal/ApproveCardModal';
import { RejectCardModal } from '@/features/card/components/RejectCardModal/RejectCardModal';
import { useRejectAccount } from '@/features/card/hooks/useCard/useRejectAccount';
import { useToast, Pagination, useSpinner } from '@chikara/ui';
import { useAuthContext } from '@/features/auth/context/AuthContext/AuthContext';
import { t } from '@/i18n';

interface CardAssignedTableProps {
    data: Card[];
    onActivateCard: () => void;
    onRefetch?: () => void;
    pagination?: any;
    basic?: boolean;
}

const formatMXN = (amount: number) => formatCurrency(amount, { currency: 'MXN', locale: 'es-MX' });

export const CardAssignedTable = ({ data, onActivateCard, onRefetch, pagination, basic }: CardAssignedTableProps) => {
    const { isSpinnerLoading } = useSpinner();
    const navigate = useNavigate();
    const toast = useToast();
    const { payload: authPayload } = useAuthContext();
    const { rejectAccount, isLoading: isRejecting } = useRejectAccount();

    const [selectedCard, setSelectedCard] = useState<Card | null>(null);
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

    const columns: Column[] = [
        { key: 'email', label: t('dashboard.card.assignedTable.email'), width: '15%' },
        { key: 'card', label: t('dashboard.card.assignedTable.card'), width: '12%' },
        { key: 'cardholderName', label: t('dashboard.card.assignedTable.user'), width: '15%' },
        { key: 'disposableParse', label: t('dashboard.card.assignedTable.disposable'), width: '12%' },
        { key: 'creditLimitParse', label: t('dashboard.card.assignedTable.limit'), width: '12%' },
        { key: 'typeParse', label: t('dashboard.card.assignedTable.type'), width: '10%' },
        { key: 'tag', label: t('dashboard.card.assignedTable.label'), width: '10%' },
        { key: 'status', label: t('dashboard.card.assignedTable.status'), width: '14%' },
    ];

    const mobileConfig: MobileConfig = {
        primaryField: 'email',
        secondaryField: 'card',
        expandableFields: ['cardholderName', 'disposable', 'creditLimit', 'typeParse', 'tag', 'status'],
    };

    const tableData = data.map(card => ({
        ...card,
        id: card.card_uuid,
        card: `${card.cardAlias} / ${card.maskedPan}`,
        disposableParse: formatMXN(card.disposable),
        creditLimitParse: formatMXN(card.creditLimit),
        typeParse: t(`dashboard.card.type.${card.type}`),
        requestDate: formatDateExact(card.requestDate),
    }));

    const handleApproveClick = (e: React.MouseEvent, card: Card) => {
        e.stopPropagation();
        setSelectedCard(card);
        setIsApproveModalOpen(true);
    };

    const handleRejectClick = (e: React.MouseEvent, card: Card) => {
        e.stopPropagation();
        setSelectedCard(card);
        setIsRejectModalOpen(true);
    };

    const handleConfirmReject = async () => {
        if (!selectedCard) return;
        
        const payload = {
            status: "rejected",
            account_uuid: selectedCard.uuid,
            user_uuid: authPayload?.user_uuid ? authPayload?.user_uuid : ""
        }

        const success = await rejectAccount(payload);
        if (success) {
            toast.success(t('dashboard.card.detail.modals.rejectCard.success'));
            onRefetch?.();
            setIsRejectModalOpen(false);
        }
    };

    if ((!tableData || tableData.length === 0) && !isSpinnerLoading) {
        return <EmptyState
            title={t('dashboard.card.emptyState.assignedTitle')}
            description={t('dashboard.card.emptyState.assignedDescription')}
            imgSrc={dataNotFound}
            buttonText={t('dashboard.card.actions.activate')}
            buttonAction={() => onActivateCard()}
        />;
    }

    return (
        <div className={styles.container}>
            <DataTable
                columns={columns}
                data={tableData}
                mobileConfig={mobileConfig}
                isLoading={isSpinnerLoading}
                onRowClick={(row) => {
                    if (row.uuid && row.status !== 'pending_approval' && row.status !== 'no_card') {
                        navigate(`${ROUTES.CARD}/${row.uuid}`, {
                            state: row,
                        });
                    }
                }}
                renderCell={(key, value, row) => {
                    if (key === 'card') {
                        return (
                            <div className={styles.cardCell}>
                                <div className={styles.cardAlias}>{row.cardAlias}</div>
                                <div className={styles.cardNumber}>{row.maskedPan}</div>
                            </div>
                        );
                    }
                    if (key === 'status') {
                        const isPending = value === 'pending_approval';
                        return (
                            <div className={styles.statusCell}>
                                <StatusField
                                    status={t(`dashboard.card.status.${value}`)}
                                    statusType={getStatusType(value as string)}
                                />
                                {isPending && !basic && (
                                    <div className={styles.actions}>
                                        <button
                                            className={`${styles.actionButton} ${styles.approveButton}`}
                                            onClick={(e) => handleApproveClick(e, row as Card)}
                                            title={t('team.approve')}
                                        >
                                            <Icon name="check" size="sm" color="success" />
                                        </button>
                                        <button
                                            className={`${styles.actionButton} ${styles.rejectButton}`}
                                            onClick={(e) => handleRejectClick(e, row as Card)}
                                            title={t('dashboard.card.detail.modals.rejectCard.confirm')}
                                        >
                                            <Icon name="close" size="sm" color="error" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    }
                    return value;
                }}
            />

            {pagination && <Pagination 
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={(page) => pagination.onPageChange(page, pagination.itemsPerPage)}
                showInfo
                infoText={t('dashboard.paginationInfo', {
                    start: (pagination.currentPage - 1) * pagination.itemsPerPage + 1,
                    end: Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems),
                    total: pagination.totalItems
                })}
            />}

            {selectedCard && (
                <>
                    <ApproveCardModal
                        isOpen={isApproveModalOpen}
                        onClose={() => setIsApproveModalOpen(false)}
                        card={selectedCard}
                        onSuccess={() => onRefetch?.()}
                    />
                    <RejectCardModal
                        isOpen={isRejectModalOpen}
                        onClose={() => setIsRejectModalOpen(false)}
                        onConfirm={handleConfirmReject}
                        isRejecting={isRejecting}
                    />
                </>
            )}
        </div>
    );
};
